import React, { useState, useMemo, useRef, useEffect } from 'react';
import Transition from 'react-transition-group/Transition';
import styled from 'styled-components';
import { getInstance as getBootstrap } from 'utils/bootstrap';
import { TRANSITION_TIMEOUT, splashLogoElement } from 'common/constants';
import { latoBoldWeight } from 'common/styles/typography';

const FloatingButtonLink = styled.a`
    position: fixed;
    bottom: 2%;
    right: 2%;
    left: auto;

    width: ${(props) => (props.buttonWidth ? `${props.buttonWidth}px` : 'auto')};

    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 0.75rem;
    z-index: 1999;

    border-radius: 2rem;
    border-width: 1.5px;
    border-style: solid;

    overflow: hidden;

    font-size: 0.75rem;
    font-weight: ${latoBoldWeight};

    background-color: ${({ theme }) => theme.palette.grayscale.white};
    color: ${({ theme }) => theme.palette.grayscale.black};
    border-color: ${({ theme }) => theme.palette.grayscale.black};

    transition: opacity ${TRANSITION_TIMEOUT}ms ease-in-out, background-color 50ms ease;
    opacity: ${(props) =>
        ({
            entering: 1,
            entered: 1,
            exiting: 0,
            exited: 0,
        }[props.styleState])};

    &:hover {
        background-color: ${({ theme }) => theme.palette.grayscale.black};
        color: ${({ theme }) => theme.palette.grayscale.white};
    }

    > svg {
        float: right;
        width: auto;
        height: 19px;
        margin-bottom: -1px; // so the bottom line of the 'p' doesn't take height
        margin-left: 0.4em;
        fill: currentColor;
    }
`;

const useFooterNodeIsInViewport = () => {
    const [footerVisible, setFooterVisible] = useState(true);
    useEffect(() => {
        if (!('IntersectionObserver' in window)) {
            // only IE doesn't have IntersectionObserver
            setFooterVisible(true);
            return undefined;
        }
        const footer = document.getElementById('footer');
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const visible = entry.intersectionRatio < 0.25;
                    setFooterVisible(visible);
                });
            },
            { threshold: [0.25] },
        );
        observer.observe(footer);
        return () => observer.disconnect();
    }, []);
    return footerVisible;
};

/**
 * Splug is the upsell button displaying "Powered by Splash" message on freemium pages
 */
const Splug = () => {
    const [hovered, setHovered] = useState(false);
    const [freemiumUpsellLink, eventId] = useMemo(() => {
        const bootstrap = getBootstrap();
        return [bootstrap.get('splug.freemiumUpsellLink'), bootstrap.get('splug.eventId')];
    }, []);

    // When the event page footer is visible, we hide the floating button so it doesn't overlap with the footer buttons
    const footerVisible = useFooterNodeIsInViewport();

    // When rendered, we measure the width and fix it, so when hovering and changing the text, the button width remains
    const floatingButton = useRef();
    const [buttonWidth, setButtonWidth] = useState();
    useEffect(() => {
        if (footerVisible && floatingButton.current) {
            const measuredInitialWidth = getComputedStyle(floatingButton.current).width;
            setButtonWidth(parseInt(measuredInitialWidth, 10) + 1);
        }
    }, [footerVisible]);

    return (
        <Transition in={footerVisible} timeout={TRANSITION_TIMEOUT / 2} appear unmountOnExit mountOnEnter>
            {(state) => (
                <FloatingButtonLink
                    href={`${freemiumUpsellLink}&utm_content=splug&utm_eventid=${eventId}`}
                    target="_blank"
                    ref={floatingButton}
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                    styleState={state}
                    buttonWidth={buttonWidth}
                >
                    {hovered ? (
                        <span>Sign Up for Free</span>
                    ) : (
                        <>
                            <span>Powered by</span>
                            {splashLogoElement}
                        </>
                    )}
                </FloatingButtonLink>
            )}
        </Transition>
    );
};

export default Splug;
